import CountrySelector, { type Country, countries } from '@/components/BrandProfile/CountrySelector'
import PasswordInput from '@/components/ui/PasswordInput/PasswordInput'
import type React from 'react'
import { useEffect, useRef } from 'react'
import styles from './styles.module.css'

interface PhoneInputWithCountryProps {
  id: string
  label: string
  placeholder: string
  phoneValue: string
  selectedCountry?: string
  error?: string
  onChange: (value: string) => void
  onCountryChange: (country: Country) => void
}

const detectCountryFromPhone = (phoneNumber: string): { code: string; dialCode: string } | null => {
  if (!phoneNumber) return null

  const normalized = phoneNumber.replace(/[\s\-()]/g, '')
  const sortedCountries = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length)

  for (const country of sortedCountries) {
    if (normalized.startsWith(country.dialCode)) {
      return country
    }
  }

  return null
}

const PhoneInputWithCountry: React.FC<PhoneInputWithCountryProps> = ({
  id,
  label,
  placeholder,
  phoneValue,
  selectedCountry,
  error,
  onChange,
  onCountryChange,
}) => {
  const previousPhoneValue = useRef(phoneValue)

  const handleCountrySelect = (country: Country) => {
    onCountryChange(country)

    const cleanPhone = phoneValue.replace(/^\+\d+\s?/, '')
    const newPhoneValue = `${country.dialCode} ${cleanPhone}`.trim()
    onChange(newPhoneValue)
  }

  const handlePhoneChange = (value: string) => {
    onChange(value)
  }

  useEffect(() => {
    if (phoneValue !== previousPhoneValue.current) {
      previousPhoneValue.current = phoneValue

      const detectedCountry = detectCountryFromPhone(phoneValue)

      if (detectedCountry && detectedCountry.code !== selectedCountry) {
        const fullCountry = countries.find((c) => c.code === detectedCountry.code)
        if (fullCountry) {
          onCountryChange(fullCountry)
        }
      }
    }
  }, [phoneValue, selectedCountry, onCountryChange])

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.inputWrapper}>
        <CountrySelector
          value={selectedCountry}
          phoneNumber={phoneValue}
          onCountryChange={handleCountrySelect}
          className={styles.countrySelector}
        />
        <div className={styles.passwordInputWrapper}>
          <PasswordInput id={id} label="" placeholder={placeholder} value={phoneValue} onChange={handlePhoneChange} error={error} />
        </div>
      </div>
    </div>
  )
}

export default PhoneInputWithCountry
